import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import TrackingMap from "../components/TrackingMap";
import {
  MessageCircle, Truck, Fuel, Calendar, CreditCard, Banknote,
  Package, CheckCircle2, Truck as TruckIcon, Home as HomeIcon, XCircle,
  Phone, User as UserIcon,
} from "lucide-react";
import GasCylinderIcon from "../components/icons/GasCylinderIcon";
import { whatsappUrl } from "../lib/whatsapp";
import { money } from "../lib/api";
import { formatEta } from "../lib/eta";
import { shortOrderId } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABEL = {
  pending: "Recibido",
  confirmed: "Confirmado",
  on_the_way: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};

// Happy-path timeline — we render every step even if the order is at an
// earlier one, so the user can see the full journey. Cancelled orders are
// handled separately.
const TIMELINE = [
  { key: "pending",    label: "Recibido",    description: "Tu pedido llegó a la empresa.", icon: Package },
  { key: "confirmed",  label: "Confirmado",  description: "La empresa confirmó tu pedido.", icon: CheckCircle2 },
  { key: "on_the_way", label: "En camino",   description: "Tu unidad salió hacia ti.",    icon: TruckIcon },
  { key: "delivered",  label: "Entregado",   description: "Pedido entregado. ¡Gracias!", icon: HomeIcon },
];

const channelIcon = (c) => c === "autotanque" ? Truck : c === "estacion" ? Fuel : GasCylinderIcon;
const channelLabel = (c) => c === "autotanque" ? "Autotanque" : c === "estacion" ? "Estación" : "Cilindrera";

export default function TrackPublic() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // ETA emitted by TrackingMap (raw Google Distance Matrix duration). We
  // stash it here so the driver card can label "Llega en ~X min" using the
  // company's `eta_tolerance_min` buffer.
  const [eta, setEta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/track/${id}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "No se pudo cargar el seguimiento");
      }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 max-w-md text-center">
          <p className="font-semibold">Este enlace no está disponible</p>
          <p className="text-sm text-neutral-500 mt-1">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-neutral-50" />;

  const primary = data.company?.primary_color || "#111";
  const contrast = "#fff";
  const driverLoc = data.driver?.current_lat && data.driver?.current_lng
    ? { lat: data.driver.current_lat, lng: data.driver.current_lng }
    : null;
  const destination = data.address?.lat && data.address?.lng
    ? { lat: data.address.lat, lng: data.address.lng }
    : null;
  const channel = data.channel || "cilindrera";
  const CIcon = channelIcon(channel);
  const waHref = data.company?.whatsapp
    ? whatsappUrl(data.company.whatsapp, `Hola ${data.company.name || "Gas2GO"}! Tengo una duda sobre mi pedido ${shortOrderId(data.order_id)}`)
    : null;

  // Build status timeline with timestamps from `status_history`. Steps that
  // haven't happened yet get `at: null` so the UI shows a muted placeholder.
  const statusAtMap = {};
  for (const ev of data.status_history || []) statusAtMap[ev.status] = ev.at;
  // Fallback: the first step's timestamp is always the creation date even
  // for orders created before we started logging history.
  if (!statusAtMap.pending) statusAtMap.pending = data.created_at;
  const currentIdx = TIMELINE.findIndex((t) => t.key === data.status);
  const isCancelled = data.status === "cancelled";
  const fmtTs = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
      return null;
    }
  };

  return (
    <div
      className="min-h-screen bg-neutral-50"
      style={{ "--company-primary": primary, "--company-primary-contrast": contrast }}
    >
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          {data.company?.logo_url ? (
            <img src={data.company.logo_url} alt="" className="h-9 w-9 rounded-lg object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center">
              <CIcon size={18} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold truncate">{data.company?.name || "Pedido"}</p>
            <p className="text-xs text-neutral-500 font-mono">#{shortOrderId(data.order_id)}</p>
          </div>
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 hover:underline"
              data-testid="public-contact-wa"
            >
              <MessageCircle size={14} />Contactar
            </a>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid="track-status-card">
          <p className="text-xs text-neutral-400 uppercase tracking-widest">Estado</p>
          <p className="font-display text-3xl font-extrabold mt-0.5">
            {isCancelled ? "Cancelado" : STEP_LABEL[data.status] || data.status}
          </p>
          {data.driver && !isCancelled && (
            <div className="mt-3 flex items-center gap-2 text-sm text-neutral-600">
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-company/10 text-[color:var(--company-primary)] border border-company/20">
                <CIcon size={11} />{channelLabel(channel)}
              </span>
              <span className="truncate">{data.driver.name}</span>
              {data.driver.vehicle_plate && <span className="text-neutral-400">· {data.driver.vehicle_plate}</span>}
            </div>
          )}

          {/* Vertical timeline of the order journey */}
          {!isCancelled ? (
            <ol className="mt-5 relative" data-testid="track-timeline">
              {TIMELINE.map((step, i) => {
                const Icon = step.icon;
                const done = currentIdx > i;
                const active = currentIdx === i;
                const ts = fmtTs(statusAtMap[step.key]);
                const isLast = i === TIMELINE.length - 1;
                return (
                  <li key={step.key} className="relative flex gap-3 pb-5 last:pb-0">
                    {/* Connector line */}
                    {!isLast && (
                      <span
                        className="absolute left-5 top-10 bottom-0 w-[2px]"
                        style={{ background: done ? primary : "#e5e5e5" }}
                        aria-hidden
                      />
                    )}
                    {/* Icon bubble */}
                    <div className="relative shrink-0 h-10 w-10" style={{ color: primary }}>
                      {active && (
                        <span
                          className="absolute inset-0 h-10 w-10 rounded-full map-pin-pulse"
                          aria-hidden
                        />
                      )}
                      <div
                        className={`relative h-10 w-10 rounded-full flex items-center justify-center ring-2 ring-white transition-colors`}
                        style={{
                          background: done || active ? primary : "#f5f5f5",
                          color: done || active ? "#fff" : "#737373",
                          boxShadow: active
                            ? `0 4px 10px rgba(0,0,0,0.18), inset 0 -2px 5px rgba(0,0,0,0.15), inset 0 2px 5px rgba(255,255,255,0.25)`
                            : undefined,
                        }}
                        data-testid={`track-step-${step.key}${active ? "-active" : ""}`}
                      >
                        <Icon size={18} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <p
                        className={`font-semibold text-sm ${
                          done || active ? "text-neutral-900" : "text-neutral-400"
                        }`}
                      >
                        {step.label}
                      </p>
                      {ts && (
                        <p className="text-[11px] font-mono text-neutral-500 mt-0.5">
                          {ts}
                        </p>
                      )}
                      <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="mt-5 flex items-center gap-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-3">
              <XCircle size={18} className="shrink-0" />
              <div>
                <p className="font-semibold">Pedido cancelado</p>
                {fmtTs(statusAtMap.cancelled) && (
                  <p className="text-xs text-red-500/80 mt-0.5">{fmtTs(statusAtMap.cancelled)}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {data.status !== "pending" && !isCancelled && destination && (
          <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
            <TrackingMap
              driverLocation={driverLoc}
              destination={destination}
              channel={channel}
              addressType={data.address?.type}
              color={primary}
              contrast={contrast}
              height={420}
              status={data.status}
              onEtaChange={setEta}
            />
            {data.status === "on_the_way" && !driverLoc && (
              <p className="text-sm text-neutral-500 text-center px-4 py-3 border-t border-neutral-100">
                Tu unidad aún no ha reportado posición. El mapa se actualiza cada 8 segundos.
              </p>
            )}
            {data.status === "delivered" && (
              <p className="text-sm text-green-700 text-center px-4 py-3 border-t border-neutral-100 bg-green-50">
                Entregado en esta ubicación. ¡Gracias por tu pedido!
              </p>
            )}
          </div>
        )}

        {data.address?.full_address && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-5 text-sm">
            <p className="text-xs text-neutral-400 uppercase tracking-widest">Entrega en</p>
            <p className="font-semibold mt-1">{data.address.full_address}</p>
            {data.address.reference && (
              <p className="text-xs text-neutral-500 mt-1">Referencia: {data.address.reference}</p>
            )}
          </div>
        )}

        {/* Driver contact card — only while the unit is en route. Surface
            quick WhatsApp + call actions because that's the moment of most
            customer anxiety (waiting). Mirrors Rappi/DiDi/Uber Eats. */}
        {data.status === "on_the_way" && data.driver && (() => {
          const etaInfo = formatEta(eta, data.company?.eta_tolerance_min || 0);
          return (
          <div
            className="bg-white rounded-2xl border border-neutral-200 p-5 fade-in"
            data-testid="driver-contact-card"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs text-neutral-400 uppercase tracking-widest">Tu repartidor</p>
              {etaInfo && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full text-white"
                  style={{ background: "var(--company-primary)" }}
                  data-testid="driver-eta-pill"
                  title="Tiempo estimado de llegada"
                >
                  Llega en ~{etaInfo.label}
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-12 w-12 rounded-full overflow-hidden border border-neutral-200 bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center shrink-0">
                {data.driver.image_url ? (
                  <img src={data.driver.image_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <UserIcon size={22} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display font-bold truncate">{data.driver.name || "Unidad de reparto"}</p>
                <p className="text-xs text-neutral-500 truncate">
                  {channelLabel(channel)}
                  {data.driver.vehicle_plate ? ` · ${data.driver.vehicle_plate}` : ""}
                </p>
              </div>
            </div>
            {(data.driver.phone || waHref) && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {data.driver.phone ? (
                  <a
                    href={whatsappUrl(data.driver.phone, `Hola! Soy del pedido #${shortOrderId(data.order_id)}`)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 h-11 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 tap-fade"
                    data-testid="driver-whatsapp-btn"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                ) : waHref ? (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 h-11 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 tap-fade"
                    data-testid="company-whatsapp-btn"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                ) : null}
                {data.driver.phone && (
                  <a
                    href={`tel:${data.driver.phone}`}
                    className="inline-flex items-center justify-center gap-1.5 h-11 rounded-xl font-semibold tap-fade"
                    style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
                    data-testid="driver-call-btn"
                  >
                    <Phone size={16} /> Llamar
                  </a>
                )}
              </div>
            )}
          </div>
          );
        })()}

        {/* Ticket-style receipt — usa el ancho completo del contenedor
            principal (`max-w-2xl`) en desktop para no quedar más angosto
            que las tarjetas de pedido. */}
        <div className="w-full">
          <div
            className="relative bg-white border border-neutral-200"
            style={{
              borderRadius: "18px",
              maskImage:
                "radial-gradient(circle at 0 50%, transparent 10px, black 10px), radial-gradient(circle at 100% 50%, transparent 10px, black 10px)",
              WebkitMaskComposite: "source-in",
              maskComposite: "intersect",
            }}
            data-testid="ticket-receipt"
          >
            {/* Header */}
            <div className="p-5 text-center border-b-2 border-dashed border-neutral-200">
              {data.company?.logo_url ? (
                <img src={data.company.logo_url} alt="" className="mx-auto h-10 w-10 rounded-lg object-cover" />
              ) : (
                <div className="mx-auto h-10 w-10 rounded-lg bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center">
                  <CIcon size={18} />
                </div>
              )}
              <p className="font-display font-extrabold text-xl mt-2">{data.company?.name || "Gas2GO"}</p>
              <p className="text-[11px] uppercase tracking-widest text-neutral-400 mt-0.5">Resumen del pedido</p>
              <p className="font-mono text-sm text-neutral-500 mt-2">#{shortOrderId(data.order_id)}</p>
            </div>

            {/* Meta */}
            <div className="px-5 py-3 text-xs grid grid-cols-2 gap-2 border-b border-dashed border-neutral-200">
              <div>
                <p className="text-neutral-400 uppercase tracking-wider">Fecha</p>
                <p className="font-semibold mt-0.5">
                  {data.created_at ? new Date(data.created_at).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-neutral-400 uppercase tracking-wider">Entrega</p>
                <p className="font-semibold mt-0.5 inline-flex items-center gap-1 justify-end">
                  {data.scheduled_at ? <Calendar size={11} /> : null}
                  {data.scheduled_at
                    ? new Date(data.scheduled_at).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" })
                    : "Inmediata"}
                </p>
              </div>
            </div>

            {/* Items */}
            <div className="px-5 py-3 border-b border-dashed border-neutral-200">
              <p className="text-[11px] text-neutral-400 uppercase tracking-widest mb-2">Productos</p>
              {(data.items || []).length === 0 ? (
                <p className="text-sm text-neutral-400">Sin productos</p>
              ) : (
                <ul className="space-y-1.5">
                  {(data.items || []).map((it, i) => {
                    const qty = it.quantity || 1;
                    const lineTotal = typeof it.line_total === "number"
                      ? it.line_total
                      : (Number(it.price) || 0) * qty;
                    const sub = it.kind === "bulk"
                      ? `por ${it.unit === "kg" ? "kg" : "litro"}`
                      : it.size_kg ? `${it.size_kg} kg` : "";
                    return (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="font-semibold shrink-0 w-7">{qty}×</span>
                        <div className="flex-1 min-w-0">
                          <p className="truncate">{it.name}</p>
                          {sub && <p className="text-[11px] text-neutral-400 leading-none mt-0.5">{sub}</p>}
                        </div>
                        <span className="font-mono text-sm">{money(lineTotal)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Totals */}
            <div className="px-5 py-3 space-y-1 text-sm">
              {typeof data.subtotal === "number" && (
                <div className="flex justify-between text-neutral-600">
                  <span>Subtotal</span><span className="font-mono">{money(data.subtotal)}</span>
                </div>
              )}
              {typeof data.delivery_fee === "number" && data.delivery_fee > 0 && (
                <div className="flex justify-between text-neutral-600">
                  <span>Envío</span><span className="font-mono">{money(data.delivery_fee)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-1 mt-1 border-t border-neutral-100">
                <span className="font-display font-extrabold text-lg">Total</span>
                <span className="font-display font-extrabold text-xl font-mono">{money(data.total)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 text-xs text-neutral-500">
                <span className="inline-flex items-center gap-1.5">
                  {(data.payment_method === "online" || data.payment_method === "stripe")
                    ? <><CreditCard size={12} /> Pago en línea</>
                    : data.payment_method === "cash"
                    ? <><Banknote size={12} /> Efectivo</>
                    : <><CreditCard size={12} /> Transferencia ({data.payment_method?.toUpperCase()})</>}
                </span>
                <span className="uppercase tracking-widest">{STEP_LABEL[data.status] || data.status}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 pt-3 pb-5 text-center text-[10px] tracking-widest uppercase text-neutral-400 border-t border-dashed border-neutral-200">
              ¡Gracias por tu pedido!
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-neutral-400">
          Página pública de seguimiento · Gas2GO
        </p>
      </main>
    </div>
  );
}
